import api from './api';

export interface User {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  can_view_transcripts?: boolean;
  exclude_from_analytics?: boolean;
  created_at: string;
  credit_balance?: number;
  subscription_plan?: string | null;
  subscription_plan_name?: string | null;
  subscription_status?: string | null;
}

export interface UserAssistantConfig {
  id?: string;
  user_id?: string;
  outbound_assistant_id?: string;
  inbound_assistant_id?: string;
  outbound_phone_number_id?: string;
  agent_name?: string;
  voice_id_male?: string;
  voice_id_female?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface UserWithConfig {
  user: User;
  assistant_config: {
    exists: boolean;
    outbound_assistant_id?: string;
    inbound_assistant_id?: string;
    outbound_phone_number_id?: string;
    agent_name?: string;
    is_active?: boolean;
    created_at?: string;
    updated_at?: string;
  };
}

export interface UserAssistantConfigCreate {
  outbound_assistant_id?: string;
  agent_name?: string;
  voice_id_male?: string;
  voice_id_female?: string;
  is_active?: boolean;
}

export interface UserAssistantConfigUpdate {
  outbound_assistant_id?: string;
  agent_name?: string;
  voice_id_male?: string;
  voice_id_female?: string;
  is_active?: boolean;
}

export interface UserDeletionStats {
  runs_deleted: number;
  leads_deleted: number;
  call_summaries_deleted: number;
  inbound_call_logs_deleted: number;
  inbound_matching_rules_deleted: number;
  inbound_configs_deleted: number;
  assistant_configs_deleted: number;
  vapi_phone_numbers_deleted: number;
  credit_transactions_deleted: number;
  credit_transactions_unlinked: number;
  credit_reserves_deleted: number;
  password_reset_tokens_deleted: number;
  email_log_deleted?: number;
  email_templates_deleted?: number;
}

export interface UserDeletionResponse {
  message: string;
  user_id: string;
  email: string;
  stats: UserDeletionStats;
}

export interface InboundConfigResponse {
  id: string;
  user_id: string;
  assistant_id: string;
  campaign_context?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface InboundConfigCreate {
  assistant_id: string;
  campaign_context?: string;
  is_active: boolean;
}

export interface CreditPackage {
  name: string;
  credits: number;
  price: number;
  description: string;
  popular?: boolean;
}

export interface SubscriptionPlan {
  name: string;
  price: number;
  call_limit: number;
  features: string[];
  description: string;
  popular?: boolean;
}

// Full item shapes returned from the new CRUD endpoints
export interface SubscriptionPlanItem {
  id: string;
  plan_id: string;
  name: string;
  price: number;
  credits: number;
  billing_interval: string;
  billing_interval_count: number;
  description: string;
  features: string[];
  is_popular: boolean;
  is_active: boolean;
  inbound_enabled: boolean;
  sms_enabled: boolean;
  sort_order: number;
}

export interface CreditPackageItem {
  id: string;
  package_id: string;
  name: string;
  price: number;
  credits: number;
  description: string;
  is_popular: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface SubscriptionPlanCreate {
  plan_id: string;
  name: string;
  price: number;
  credits: number;
  billing_interval?: string;
  billing_interval_count?: number;
  description?: string;
  features?: string[];
  is_popular?: boolean;
  inbound_enabled?: boolean;
  sms_enabled?: boolean;
  sort_order?: number;
}

export interface SubscriptionPlanUpdate {
  name?: string;
  price?: number;
  credits?: number;
  billing_interval?: string;
  billing_interval_count?: number;
  description?: string;
  features?: string[];
  is_popular?: boolean;
  inbound_enabled?: boolean;
  sms_enabled?: boolean;
  sort_order?: number;
}

export interface CreditPackageCreate {
  package_id: string;
  name: string;
  price: number;
  credits: number;
  description?: string;
  is_popular?: boolean;
  sort_order?: number;
}

export interface CreditPackageUpdate {
  name?: string;
  price?: number;
  credits?: number;
  description?: string;
  is_popular?: boolean;
  sort_order?: number;
}

export interface PricingSettings {
  trial_credits: number;
  seconds_per_credit_test: number;
  seconds_per_credit_normal: number;
  credits_per_sms: number;
  credits_per_email: number;
  credit_packages: { [key: string]: CreditPackage };
  subscription_plans: { [key: string]: SubscriptionPlan };
  currency: string;
}

export interface PricingSettingsUpdate {
  trial_credits?: number;
  seconds_per_credit_test?: number;
  seconds_per_credit_normal?: number;
  credits_per_sms?: number;
  credits_per_email?: number;
  credit_packages?: { [key: string]: CreditPackage };
  subscription_plans?: { [key: string]: SubscriptionPlan };
  currency?: string;
}

export interface GlobalAssistantConfig {
  global_outbound_assistant_id: string | null;
  global_inbound_assistant_id: string | null;
  global_phone_number_id: string | null;
  global_voice_id_male?: string | null;
  global_voice_id_female?: string | null;
}

export interface GlobalAssistantConfigUpdate {
  global_outbound_assistant_id: string;
  global_inbound_assistant_id: string;
  global_phone_number_id: string;
  global_voice_id_male?: string;
  global_voice_id_female?: string;
}

export interface SmsSettings {
  global_sms_from_phone: string | null;
}

export interface SmsSettingsUpdate {
  global_sms_from_phone: string;
}

export interface LegalSettings {
  privacy_markdown: string | null;
  terms_markdown: string | null;
}

export interface LegalSettingsUpdate {
  privacy_markdown?: string | null;
  terms_markdown?: string | null;
}

class AdminService {
  private baseUrl = '/api/admin';

  async getAllUsers(): Promise<User[]> {
    const response = await api.get(`${this.baseUrl}/users`);
    return response.data;
  }

  async getUsersWithConfigs(): Promise<UserWithConfig[]> {
    const response = await api.get(`${this.baseUrl}/users-with-configs`);
    return response.data;
  }

  async getUserAssistantConfig(userId: string): Promise<UserAssistantConfig> {
    const response = await api.get(`${this.baseUrl}/users/${userId}/assistant-config`);
    return response.data;
  }

  async createUserAssistantConfig(userId: string, config: UserAssistantConfigCreate): Promise<UserAssistantConfig> {
    const response = await api.post(`${this.baseUrl}/users/${userId}/assistant-config`, config);
    return response.data;
  }

  async updateUserAssistantConfig(userId: string, config: UserAssistantConfigUpdate): Promise<UserAssistantConfig> {
    const response = await api.put(`${this.baseUrl}/users/${userId}/assistant-config`, config);
    return response.data;
  }

  async deleteUserAssistantConfig(userId: string): Promise<{ message: string }> {
    const response = await api.delete(`${this.baseUrl}/users/${userId}/assistant-config`);
    return response.data;
  }

  async deleteUser(userId: string): Promise<UserDeletionResponse> {
    console.log('[adminService.deleteUser] request userId=', userId);
    try {
      const response = await api.delete(`${this.baseUrl}/users/${userId}`);
      console.log('[adminService.deleteUser] response status=', response.status, 'data=', response.data);
      return response.data;
    } catch (error: any) {
      console.log('[adminService.deleteUser] error=', {
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
      });
      throw error;
    }
  }

  async getUserInboundConfig(userId: string): Promise<InboundConfigResponse | null> {
    try {
      const response = await api.get(`${this.baseUrl}/users/${userId}/inbound-config`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async setUserInboundConfig(userId: string, config: InboundConfigCreate): Promise<InboundConfigResponse> {
    const response = await api.post(`${this.baseUrl}/users/${userId}/inbound-config`, config);
    return response.data;
  }

  async getUserDefaultPrompt(userId: string): Promise<{ content: string }> {
    const response = await api.get(`${this.baseUrl}/users/${userId}/default-prompt`);
    return response.data;
  }

  async setUserDefaultPrompt(userId: string, content: string): Promise<{ content: string }> {
    const response = await api.put(`${this.baseUrl}/users/${userId}/default-prompt`, { content });
    return response.data;
  }

  async addCreditsToUser(userId: string, amount: number, description?: string): Promise<{ message: string; new_balance: number }> {
    const response = await api.post(`${this.baseUrl}/users/${userId}/credits`, { amount, description });
    return response.data;
  }

  async updateUserTranscriptAccess(userId: string, canViewTranscripts: boolean): Promise<{ message: string; user_id: string; can_view_transcripts: boolean }> {
    const response = await api.patch(`${this.baseUrl}/users/${userId}/transcript-access`, {
      can_view_transcripts: canViewTranscripts,
    });
    return response.data;
  }

  async updateUserAnalyticsExclusion(userId: string, excludeFromAnalytics: boolean): Promise<{ message: string; user_id: string; exclude_from_analytics: boolean }> {
    const response = await api.patch(`${this.baseUrl}/users/${userId}/analytics-exclusion`, {
      exclude_from_analytics: excludeFromAnalytics,
    });
    return response.data;
  }

  // Pricing Settings
  async getPricingSettings(): Promise<PricingSettings> {
    const response = await api.get(`${this.baseUrl}/settings/pricing`);
    return response.data;
  }

  async updatePricingSettings(settings: PricingSettingsUpdate): Promise<PricingSettings> {
    const response = await api.put(`${this.baseUrl}/settings/pricing`, settings);
    return response.data;
  }

  // Subscription Plans CRUD
  async listSubscriptionPlans(): Promise<SubscriptionPlanItem[]> {
    const response = await api.get(`${this.baseUrl}/settings/subscription-plans`);
    return response.data;
  }

  async addSubscriptionPlan(plan: SubscriptionPlanCreate): Promise<SubscriptionPlanItem> {
    const response = await api.post(`${this.baseUrl}/settings/subscription-plans`, plan);
    return response.data;
  }

  async updateSubscriptionPlan(planId: string, plan: SubscriptionPlanUpdate): Promise<SubscriptionPlanItem> {
    const response = await api.put(`${this.baseUrl}/settings/subscription-plans/${planId}`, plan);
    return response.data;
  }

  async toggleArchiveSubscriptionPlan(planId: string): Promise<SubscriptionPlanItem> {
    const response = await api.patch(`${this.baseUrl}/settings/subscription-plans/${planId}/archive`);
    return response.data;
  }

  async deleteSubscriptionPlan(planId: string, force: boolean = false): Promise<{ message: string; affected_users?: number }> {
    const response = await api.delete(`${this.baseUrl}/settings/subscription-plans/${planId}`, { params: { force } });
    return response.data;
  }

  // Credit Packages CRUD
  async listCreditPackages(): Promise<CreditPackageItem[]> {
    const response = await api.get(`${this.baseUrl}/settings/credit-packages`);
    return response.data;
  }

  async addCreditPackage(pkg: CreditPackageCreate): Promise<CreditPackageItem> {
    const response = await api.post(`${this.baseUrl}/settings/credit-packages`, pkg);
    return response.data;
  }

  async updateCreditPackage(packageId: string, pkg: CreditPackageUpdate): Promise<CreditPackageItem> {
    const response = await api.put(`${this.baseUrl}/settings/credit-packages/${packageId}`, pkg);
    return response.data;
  }

  async toggleArchiveCreditPackage(packageId: string): Promise<CreditPackageItem> {
    const response = await api.patch(`${this.baseUrl}/settings/credit-packages/${packageId}/archive`);
    return response.data;
  }

  async deleteCreditPackage(packageId: string): Promise<{ message: string }> {
    const response = await api.delete(`${this.baseUrl}/settings/credit-packages/${packageId}`);
    return response.data;
  }

  // Global Assistant Config
  async getGlobalAssistantConfig(): Promise<GlobalAssistantConfig> {
    const response = await api.get(`${this.baseUrl}/settings/global-assistant-config`);
    return response.data;
  }

  async updateGlobalAssistantConfig(config: GlobalAssistantConfigUpdate): Promise<GlobalAssistantConfig> {
    const response = await api.put(`${this.baseUrl}/settings/global-assistant-config`, config);
    return response.data;
  }

  // SMS Settings
  async getSmsSettings(): Promise<SmsSettings> {
    const response = await api.get(`${this.baseUrl}/settings/sms`);
    return response.data;
  }

  async updateSmsSettings(settings: SmsSettingsUpdate): Promise<SmsSettings> {
    const response = await api.put(`${this.baseUrl}/settings/sms`, settings);
    return response.data;
  }

  // Legal (Privacy & Terms)
  async getLegalSettings(): Promise<LegalSettings> {
    const response = await api.get(`${this.baseUrl}/settings/legal`);
    return response.data;
  }

  async updateLegalSettings(settings: LegalSettingsUpdate): Promise<LegalSettings> {
    const response = await api.put(`${this.baseUrl}/settings/legal`, settings);
    return response.data;
  }

  // Phone Pool
  async getPhonePool(statusFilter?: string, numberType?: string, countryCode?: string): Promise<PhonePoolNumber[]> {
    const params: Record<string, string> = {};
    if (statusFilter) params.status_filter = statusFilter;
    if (numberType) params.number_type = numberType;
    if (countryCode) params.country_code = countryCode;
    const response = await api.get(`${this.baseUrl}/phone-pool`, { params });
    return response.data;
  }

  async getPhonePoolStats(): Promise<PhonePoolStats> {
    const response = await api.get(`${this.baseUrl}/phone-pool/stats`);
    return response.data;
  }

  async provisionPhoneNumbers(count: number, countryCode: string = 'AU', numberType: string = 'mobile'): Promise<PhonePoolProvisionResult> {
    const response = await api.post(`${this.baseUrl}/phone-pool/provision`, { count, country_code: countryCode, number_type: numberType });
    return response.data;
  }

  async updatePoolNumber(poolId: string, action: 'retire' | 'reactivate'): Promise<{ message: string }> {
    const response = await api.patch(`${this.baseUrl}/phone-pool/${poolId}`, null, { params: { action } });
    return response.data;
  }

  async testTwilioConnection(): Promise<TwilioTestResult> {
    const response = await api.get(`${this.baseUrl}/phone-pool/test-twilio`);
    return response.data;
  }

  async getVoiceProvider(): Promise<{ provider: string }> {
    const response = await api.get(`${this.baseUrl}/phone-pool/voice-provider`);
    return response.data;
  }

  async syncTelnyxNumbers(): Promise<TelnyxSyncResult> {
    const response = await api.post(`${this.baseUrl}/phone-pool/sync-telnyx`);
    return response.data;
  }


  async getUserPoolNumber(userId: string): Promise<PhonePoolNumber | null> {
    try {
      const response = await api.get(`${this.baseUrl}/users/${userId}/pool-number`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }
}

export interface PhonePoolNumber {
  id: string;
  vapi_phone_number_id: string | null;
  phone_number: string;
  number_type: string;
  country_code: string;
  provider: string;
  status: 'available' | 'assigned' | 'retired';
  assigned_to_user_id: string | null;
  assigned_to_user_email: string | null;
  assigned_at: string | null;
  server_url_configured: boolean;
  provisioned_automatically: boolean;
  created_at: string;
  updated_at: string | null;
  twilio_sid: string | null;
}

export interface PhonePoolStats {
  total: number;
  available: number;
  assigned: number;
  retired: number;
  by_type: Record<string, Record<string, number>>;
}

export interface PhonePoolProvisionResult {
  provisioned: number;
  numbers: Array<{ phone: string; vapi_id: string; twilio_sid: string }>;
}

export interface TelnyxSyncResult {
  added: number;
  retired: number;
  errors: string[];
}

export interface TwilioTestResult {
  configured: boolean;
  connected: boolean;
  account_name?: string;
  error?: string;
}

export const adminService = new AdminService();
